import {
  Action, FormHandlers, ModalSize, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import React from 'react';
import { AxiosError } from 'axios';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { Property, PublishedOn } from '../../../providers/api/dtos';
import { aggregatorLabels } from '../../constants';
import PublishedOnPromotionForm, { PublishedOnPromotionFormModel } from '../../components/published-on-promotion-form/PublishedOnPromotionForm';
import { Aggregator, AggregatorPromotion, Status } from '../../../domain/types';
import { updatePublishedOn } from '../../../providers/api/publishedOn/publishedOnProvider';

interface ChildFnProps {
  edit: VoidFunction,
  isSaving: boolean,
}

export interface EditPublishedOnPromotionActionProps extends React.AriaAttributes {
  property: Partial<Pick<Property, 'status'>>,
  publishedOn: PublishedOn,
  width?: number | string,
  onSuccess?: (publishedOn: PublishedOn) => void,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

export const EDIT_PUBLISHED_ON_PROMOTION_SUCCESS_MESSAGE = "La tipologia dell'annuncio è stata modificata con successo";
export const EDIT_PUBLISHED_ON_PROMOTION_ERROR_MESSAGE = "Non è stato possibile modificare la tipologia dell'annuncio";
export const EDIT_PUBLISHED_ON_PROMOTION_SLOT_EXAUSTED_ERROR_MESSAGE = "Hai superato il numero massimo di vetrine consentite dal contratto. Rimuovi una vetrina da un altro immobile prima di procedere con l'inserimento e il salvataggio.";

const EditPublishedOnPromotionAction: React.FC<EditPublishedOnPromotionActionProps> = ({
  property,
  publishedOn,
  width,
  onSuccess,
  children,
}) => {
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const allowedPromotions = React.useMemo(() => {
    switch (publishedOn.aggregator) {
      case Aggregator.IMMOBILIARE_IT:
        return [
          AggregatorPromotion.VETRINA,
          AggregatorPromotion.SKY,
          AggregatorPromotion.STAR,
          AggregatorPromotion.TOP,
        ];
      case Aggregator.DOVE_IT:
        return [AggregatorPromotion.PREMIUM];
      default:
        return [];
    }
  }, [publishedOn.aggregator]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  const onSubmit = React.useCallback(async (formValues: PublishedOnPromotionFormModel) => {
    try {
      setIsSaving(true);

      const publishedOnToUpdate: PublishedOn = {
        ...publishedOn,
        aggregatorPromotion: ['', 'NO'].includes(formValues.promotion)
          ? undefined
          : formValues.promotion as AggregatorPromotion,
      };

      const updatedPublishedOn = await updatePublishedOn(publishedOnToUpdate);

      setIsSaving(false);
      addSuccess(EDIT_PUBLISHED_ON_PROMOTION_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(updatedPublishedOn);
    } catch (error) {
      setIsSaving(false);
      addError(
        error instanceof AxiosError && error.response?.status === 412
          ? EDIT_PUBLISHED_ON_PROMOTION_SLOT_EXAUSTED_ERROR_MESSAGE
          : EDIT_PUBLISHED_ON_PROMOTION_ERROR_MESSAGE,
      );
    }
  }, [addError, addSuccess, modal, onSuccess, publishedOn]);

  if (property.status !== Status.LIVE || allowedPromotions.length === 0) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          edit: modal.open,
          isSaving,
        })
        : (
          <Action
            aria-label="Modifica i dati dell'annuncio"
            label="Modifica annuncio"
            size="S"
            onClick={() => modal.open()}
            loading={isSaving}
            width={width}
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`Modifica annuncio su ${aggregatorLabels[publishedOn.aggregator]}`}
          aria-label="Modale per modificare i dati dell'annuncio"
          size={ModalSize.LARGE}
          footer={(
            <Action
              label="Modifica"
              color="primary"
              emphasis="high"
              aria-label="Pulsante per modificare i dati dell'annuncio"
              onClick={submitForm}
              loading={isSaving}
            />
          )}
        >
          <PublishedOnPromotionForm
            allowedPromotions={allowedPromotions}
            innerRef={formRef}
            initialValues={{
              promotion: publishedOn.aggregatorPromotion || 'NO',
            }}
            onSubmit={onSubmit}
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default EditPublishedOnPromotionAction;
